import React, { useState, useEffect } from 'react';
import { Card, Steps, Button, Form, message, Spin } from 'antd';
import { FormOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import TopicSelector from '../components/TopicSelector';
import StudentEssayTable from '../components/StudentEssayTable';
import { essayAPI } from '../services/api';

const { Step } = Steps;
// const { TextArea } = Input;

interface BatchInfo {
  batchId?: string;
  topicId?: number;
  topic?: any;
  essayType: 'continuation' | 'application';
}

const BatchCorrectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId?: string }>();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [batchInfo, setBatchInfo] = useState<BatchInfo>({
    topicId: undefined,
    topic: undefined,
    essayType: 'continuation',
  });
  const [essays, setEssays] = useState<any[]>([]);
  const [form] = Form.useForm();

  // 加载批次数据
  useEffect(() => {
    if (batchId) {
      loadBatchData(batchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // 从后端加载批次数据
  const loadBatchData = async (id: string) => {
    setLoading(true);
    try {
      const response = await essayAPI.getBatch(id);
      const batchEssays = response.data.essays || response.data; // 兼容两种格式

      if (batchEssays && Array.isArray(batchEssays) && batchEssays.length > 0) {
        // 从第一个作文中恢复批次信息
        const firstEssay = batchEssays[0];
        setBatchInfo({
          batchId: id,
          topicId: firstEssay.topic_id,
          topic: firstEssay.topic,
          essayType: firstEssay.essay_type || 'continuation'
        });

        // 转换后端数据为前端格式
        const convertedEssays = batchEssays.map((essay: any) => ({
          rowId: `essay-${essay.id}`,
          id: essay.id,
          studentId: essay.student_id,
          studentName: essay.student?.name || '',
          className: essay.class?.name || '',
          classId: essay.class_id,
          fileName: essay.original_file ? essay.original_file.split('/').pop() : undefined,
          status: mapBackendStatusToFrontend(essay.status),
          ocrText: essay.corrected_text || essay.original_text || '',
        }));

        setEssays(convertedEssays);
        setCurrent(1); // 直接跳到第二步

        // 更新表单值
        form.setFieldsValue({
          topicId: firstEssay.topic_id,
          essayType: firstEssay.essay_type || 'continuation'
        });
      }
    } catch (error) {
      message.error('加载批次数据失败');
      // 加载失败时返回新建页面
      navigate('/batch-correction');
    } finally {
      setLoading(false);
    }
  };

  // 将后端状态映射到前端显示状态
  const mapBackendStatusToFrontend = (backendStatus: string): string => {
    const statusMap: Record<string, string> = {
      'pending': 'pending',
      'uploaded': 'uploaded',
      'processing': 'uploaded',
      'recognizing': 'recognizing',
      'recognized': 'recognized',
      'correcting': 'correcting',
      'completed': 'completed',
      'failed': 'failed'
    };
    return statusMap[backendStatus] || 'pending';
  };

  const steps = [
    { 
      title: '设置作文信息', 
      description: '选择或输入作文题目和要求',
      icon: <FormOutlined />
    },
    { 
      title: '批量管理作文', 
      description: '添加学生并上传作文',
      icon: <TeamOutlined />
    },
  ];

  // 第一步：设置作文信息
  const renderStep1 = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 1200, margin: '0 auto' }}
        initialValues={batchInfo}
        onFinish={(values) => {
          setBatchInfo({
            ...batchInfo,
            ...values
          });
          setCurrent(1);
        }}
      >
        <Form.Item
          name="topicId"
          label="作文题目"
          rules={[{ required: true, message: '请输入或选择作文题目' }]}
          style={{ marginBottom: 24 }}
        >
          <TopicSelector
            onChange={(topicId, topic) => {
              if (topic) {
                form.setFieldsValue({
                  topicId: topic.id
                });
                setBatchInfo({
                  ...batchInfo,
                  topicId: topic.id,
                  topic: topic,
                  essayType: topic.essay_type || 'continuation'
                });
              }
            }}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" size="large">
            下一步：添加学生作文
          </Button>
        </Form.Item>
      </Form>
    );
  };

  // 第二步：批量管理作文
  const renderStep2 = () => {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 20, overflow: 'hidden' }}>
          <span style={{ fontSize: 18, fontWeight: 500 }}>批量管理作文</span>
          <span style={{ marginLeft: 16, fontSize: 14, color: '#666' }}>
            题目：{batchInfo.topic?.title || '未设置'}
          </span>
          <Button
            onClick={() => setCurrent(0)}
            style={{ float: 'right' }}
          >
            返回修改题目
          </Button>
        </div>
        <StudentEssayTable
          batchInfo={batchInfo}
          essays={essays}
          onEssaysChange={setEssays}
          onBatchCreated={(newBatchId) => {
            setBatchInfo({
              ...batchInfo,
              batchId: newBatchId
            });
            // 更新URL但不重新加载页面
            navigate(`/batch-correction/${newBatchId}`, { replace: true });
          }}
        />
      </div>
    );
  };

  // 渲染当前步骤内容
  const renderStepContent = () => {
    switch (current) {
      case 0:
        return renderStep1();
      case 1:
        return renderStep2();
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card>
        <Steps current={current}>
          {steps.map((item, index) => (
            <Step
              key={index}
              title={item.title}
              description={item.description}
              icon={item.icon}
            />
          ))}
        </Steps>

        <div style={{ marginTop: 40 }}>
          {loading ? (
            <div style={{ textAlign: 'center' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>处理中...</div>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>
      </Card>
    </div>
  );
};

export default BatchCorrectionPage;
